import {
  Component,
  DestroyRef,
  TemplateRef,
  ViewChild,
  inject,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { InputWrapperComponent } from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import { DeviceItemCardComponent } from '../device-item-card/device-item-card.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { DeviceItemRowComponent } from '../device-item-row/device-item-row.component';
import { DeviceType } from '@birco/rainmesh-api';
import { Store } from '@ngrx/store';
import {
  selectDeviceFilter,
  selectDevicesTotalCount,
  selectFilteredDevicesWithCurrentPage,
  selectIsDevicesLoading,
} from '../../state/device.selectors';
import { PageEvent } from '@angular/material/paginator';
import { catchError, EMPTY, take } from 'rxjs';
import {
  DeviceFilter,
  DeviceSortBy,
  DeviceSortDir,
} from '../../state/device.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { LabelConverter } from '../../../../shared/converter/label.converter';
import { withResetPageOnOtherFilterChange } from '../../../../shared/utils/filter-utils';
import { DeviceActions } from '../../state/device.actions';
import { selectIsSystemRunning } from '../../../dashboard/state/kill-switch.selectors';
import { DeviceResponseDto } from '../../models/device-response.interface';
import { ViewPreferenceService } from '../../../../core/services/view-preference.service';
import { Router } from '@angular/router';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { MatDialog } from '@angular/material/dialog';
import { FilterDialogComponent } from '../../../../shared/components/filter-dialog/filter-dialog.component';

type DeviceListScope = 'favorite' | 'inService' | 'outOfService';
type DeviceSortOrder =
  | 'nameAsc'
  | 'nameDesc'
  | 'lastSeenDateAsc'
  | 'lastSeenDateDesc';

@Component({
  selector: 'app-tab-device-list',
  imports: [
    TranslatePipe,
    IconComponent,
    InputWrapperComponent,
    ReactiveFormsModule,
    DeviceItemCardComponent,
    PaginationComponent,
    DeviceItemRowComponent,
    AsyncPipe,
    NgTemplateOutlet,
  ],
  templateUrl: './tab-device-list.component.html',
  styleUrl: './tab-device-list.component.scss',
})
export class TabDeviceListComponent implements OnInit {
  private static readonly viewPreferenceKey = 'devices.tab-device-list';
  private _fb = inject(FormBuilder);
  private store = inject(Store);
  private readonly viewPreferenceService = inject(ViewPreferenceService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  private destroyRef = inject(DestroyRef);

  loading$ = this.store.select(selectIsDevicesLoading);
  totalCount$ = this.store.select(selectDevicesTotalCount);
  deviceList$ = this.store.select(selectFilteredDevicesWithCurrentPage);
  killSwitch$ = this.store.select(selectIsSystemRunning);
  @ViewChild('filterFields', { static: true })
  filterFieldsTemplate?: TemplateRef<unknown>;
  @ViewChild('filterDialogFields', { static: true })
  filterDialogFieldsTemplate?: TemplateRef<unknown>;

  isListView = true;
  isFilterDialogOpen = false;
  filterForm: FormGroup;
  filterOptions = {
    deviceType: [
      { label: 'default.all', value: null },
      ...Object.values(DeviceType)
        .filter((value) => value !== DeviceType.PUMP)
        .map((value) => ({
          label: LabelConverter.getDeviceTypeLabel(value),
          value: value,
        })),
    ],
    listScope: [
      {
        label: 'devices.filter.listScope.inService',
        value: 'inService' as const,
      },
      {
        label: 'devices.filter.listScope.favorite',
        value: 'favorite' as const,
      },
      {
        label: 'devices.filter.listScope.outOfService',
        value: 'outOfService' as const,
      },
    ],
    sortOrder: [
      { label: 'devices.filter.sort.nameAsc', value: 'nameAsc' as const },
      { label: 'devices.filter.sort.nameDesc', value: 'nameDesc' as const },
      {
        label: 'devices.filter.sort.lastSeenDateDesc',
        value: 'lastSeenDateDesc' as const,
      },
      {
        label: 'devices.filter.sort.lastSeenDateAsc',
        value: 'lastSeenDateAsc' as const,
      },
    ],
  };

  constructor() {
    this.filterForm = this._fb.group({
      searchTerm: [undefined],
      deviceType: [null],
      listScope: ['inService' as DeviceListScope],
      sortOrder: ['nameAsc' as DeviceSortOrder],
      page: [0],
      pageSize: [12],
    });
    this.isListView = this.viewPreferenceService.getIsListView(
      TabDeviceListComponent.viewPreferenceKey,
      true,
    );
  }

  onChangePage(p: PageEvent) {
    this.filterForm.controls['page'].patchValue(p.pageIndex);
  }

  ngOnInit() {
    this.filterForm.valueChanges
      .pipe(
        withResetPageOnOtherFilterChange(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((newFilter) => {
        this.dispatchFilterUpdate(newFilter);
      });

    this.store
      .select(selectDeviceFilter)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((_) => EMPTY),
      )
      .subscribe((filter) => {
        this.filterForm.patchValue(
          {
            ...filter,
            deviceType: this.mapFilterToDeviceType(filter),
            listScope: this.mapFilterToListScope(filter),
            sortOrder: this.mapFilterToSortOrder(
              filter.sortBy ?? 'name',
              filter.sortDir ?? 'asc',
            ),
          },
          { emitEvent: false },
        );
      });

    queueMicrotask(() => {
      this.dispatchFilterUpdate(this.filterForm.getRawValue());
    });
  }

  onTriggerDeviceAction(evtData: { id: string; value: boolean }) {
    this.store.dispatch(
      DeviceActions.public.triggerDeviceAction({
        id: evtData.id,
        value: evtData.value,
      }),
    );
  }

  onDeleteDevice(id: string) {
    this.store.dispatch(DeviceActions.public.deleteDeviceById({ id: id }));
  }

  onEditDevice(id: string) {
    this.router.navigate(['/', RouteConstants.routeDevice, id], {
      queryParams: { edit: true },
    });
  }

  onToggleFavorite(
    device: Pick<DeviceResponseDto, 'id' | 'name' | 'isFavorite'>,
  ) {
    this.store.dispatch(
      DeviceActions.public.updateDevice({
        id: device.id,
        isFavorite: !device.isFavorite,
      }),
    );
  }

  toggleView() {
    this.isListView = !this.isListView;
    this.viewPreferenceService.setIsListView(
      TabDeviceListComponent.viewPreferenceKey,
      this.isListView,
    );
  }

  openFilterDialog() {
    if (!this.filterDialogFieldsTemplate || this.isFilterDialogOpen) return;
    this.isFilterDialogOpen = true;

    const dialogRef = this.dialog.open(FilterDialogComponent, {
      width: 'min(34rem, calc(100vw - 2rem))',
      maxWidth: '100vw',
      data: {
        titleKey: 'default.filter',
        contentTemplate: this.filterDialogFieldsTemplate,
      },
    });

    this.filterForm.valueChanges
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => dialogRef.close());

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isFilterDialogOpen = false;
      });
  }

  private mapFilterToDeviceType(filter: {
    deviceTypes?: DeviceType[];
  }): DeviceType | null {
    // A single-element array is an explicit user selection; the multi-element
    // "all types except PUMP" default filter must round-trip back to "All" (null).
    return filter.deviceTypes?.length === 1 ? filter.deviceTypes[0] : null;
  }

  private mapFilterToListScope(filter: {
    isFavorite?: boolean;
    isOutOfService?: boolean;
  }): DeviceListScope {
    if (filter.isFavorite === true) {
      return 'favorite';
    }

    if (filter.isOutOfService === true) {
      return 'outOfService';
    }

    return 'inService';
  }

  private dispatchFilterUpdate(rawFilter: {
    searchTerm?: string;
    deviceType?: DeviceType | null;
    listScope?: DeviceListScope;
    sortOrder?: DeviceSortOrder;
    page?: number | null;
    pageSize?: number | null;
  }) {
    this.store.dispatch(
      DeviceActions.public.updateDeviceFilter({
        filter: this.mapFormToDeviceFilter(rawFilter),
      }),
    );
  }

  private mapFormToDeviceFilter(rawFilter: {
    searchTerm?: string;
    deviceType?: DeviceType | null;
    listScope?: DeviceListScope;
    sortOrder?: DeviceSortOrder;
    page?: number | null;
    pageSize?: number | null;
  }): DeviceFilter {
    const { sortBy, sortDir } = this.mapSortOrderToFilterSort(
      rawFilter.sortOrder ?? 'nameAsc',
    );
    const filter: DeviceFilter = {
      searchTerm: rawFilter.searchTerm,
      sortBy,
      sortDir,
      page: rawFilter.page ?? undefined,
      pageSize: rawFilter.pageSize ?? undefined,
    };

    if (rawFilter.deviceType) {
      filter.deviceTypes = [rawFilter.deviceType];
    } else {
      // Pumps have their own dedicated page and must never appear in the general devices list.
      filter.deviceTypes = Object.values(DeviceType).filter((type) => type !== DeviceType.PUMP);
    }

    if (rawFilter.listScope === 'favorite') {
      filter.isFavorite = true;
    } else if (rawFilter.listScope === 'inService') {
      filter.isOutOfService = false;
    } else if (rawFilter.listScope === 'outOfService') {
      filter.isOutOfService = true;
    }

    return filter;
  }

  private mapSortOrderToFilterSort(sortOrder: DeviceSortOrder): {
    sortBy: DeviceSortBy;
    sortDir: DeviceSortDir;
  } {
    switch (sortOrder) {
      case 'nameDesc':
        return { sortBy: 'name', sortDir: 'desc' };
      case 'lastSeenDateAsc':
        return { sortBy: 'lastSeenDate', sortDir: 'asc' };
      case 'lastSeenDateDesc':
        return { sortBy: 'lastSeenDate', sortDir: 'desc' };
      case 'nameAsc':
      default:
        return { sortBy: 'name', sortDir: 'asc' };
    }
  }

  private mapFilterToSortOrder(
    sortBy: DeviceSortBy,
    sortDir: DeviceSortDir,
  ): DeviceSortOrder {
    if (sortBy === 'lastSeenDate' && sortDir === 'asc') {
      return 'lastSeenDateAsc';
    }

    if (sortBy === 'lastSeenDate' && sortDir === 'desc') {
      return 'lastSeenDateDesc';
    }

    if (sortBy === 'name' && sortDir === 'desc') {
      return 'nameDesc';
    }

    return 'nameAsc';
  }
}
